import { buildURLSearchParams } from '../helpers/format';
import { EXTERNAL_CONNECTION_CONFIGS } from '../components/content/external/config/externalConnectionConfigs';
import onApiRequest from './ApiRequestHandler';
import createDataProvider from './createDataProvider';
import { providerContextReducer } from './providerContextReducer';

const initialPaginatedState = {
  pageIndex: 1,
  pageSize: 50,
  totalCount: 0,
  totalPages: 0,
  totalNoFilterCount: 0,
  data: [],
  filter: null,
  loading: false,
  error: null
};

const initialState = {
  ecomAdminAnalyticsOverview: { value: null, loading: false, error: null },
  ecomAdminAnalytics: { value: null, loading: false, error: null },
  ecomAdminReport: { value: null, loading: false, error: null },
  ecomAdminStoreEscrow: { value: null, loading: false, error: null },
  ecomAdminStoreEscrowOverview: { value: null, loading: false, error: null },
  ecomCustomer: { value: null, loading: false, error: null },
  ecomExternalProduct: { value: null, loading: false, error: null },
  ecomExternalProductCreateOptions: { value: [], loading: false, error: null },
  ecomExternalProducts: { value: [], loading: false, error: null },
  ecomExternalProductsExport: { value: [], loading: false, error: null },
  ecomResourceOptions: { value: null, loading: false, error: null },
  ecomStockContent: { value: null, loading: false, error: null },
  ecomStore: { value: null, loading: false, error: null },
  ecomStores: { value: [], loading: false, error: null },
  ecomVendor: { value: null, loading: false, error: null },
  ecomVendorPayout: { value: null, loading: false, error: null },
  paginatedEcomCustomers: { ...initialPaginatedState },
  paginatedEcomExternalProducts: { ...initialPaginatedState },
  paginatedEcomOrders: { ...initialPaginatedState },
  paginatedEcomStockContent: { ...initialPaginatedState },
  paginatedEcomStockContentStores: { ...initialPaginatedState },
  paginatedEcomStores: { ...initialPaginatedState },
  paginatedEcomVendorPayouts: { ...initialPaginatedState },
  paginatedEcomVendors: { ...initialPaginatedState },
  userSearchResults: { value: [], loading: false, error: null }
};

// #region Users
const searchForUsers = (dispatch) => async (
  { searchTerm, skipStateUpdate = false },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/user/search`,
      type: 'get',
      params: { term: searchTerm },
      ...(skipStateUpdate
        ? {}
        : {
            dispatch,
            stateName: 'userSearchResults',
            successAction: 'success'
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region General
const fetchAdminResourceOptions = (dispatch) => async (
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomResourceOptions',
      url: `/api/staff/ecommerce/resources/options`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Stores
const fetchEcomStore = (dispatch) => async (
  pkEcomStore,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomStore',
      url: `/api/staff/ecommerce/stores/${pkEcomStore}`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchEcomStores = (dispatch) => async (
  pkEcomStore,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomStores',
      url: `/api/staff/ecommerce/stores/${pkEcomStore}`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedEcomStores = (dispatch) => async (
  { search, status, teamSearch, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomStores',
      url: `/api/staff/ecommerce/stores/summaries`,
      type: 'get',
      params: {
        search,
        status,
        teamSearch,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminStoreEscrowOverview = (dispatch) => async (
  { pkEcomStore } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: pkEcomStore
        ? `ecomAdminStoreEscrow`
        : 'ecomAdminStoreEscrowOverview',
      url: `/api/staff/ecommerce/stores/escrow-balance`,
      type: 'get',
      params: { pkEcomStore },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createOfflineEcomStoreEscrowPayment = (dispatch) => async (
  pkEcomStore,
  { fkRegFormSubmissionPaymentType, transactionId },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/stores/${pkEcomStore}/offline-payment`,
      type: 'post',
      data: { fkRegFormSubmissionPaymentType, transactionId }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region External Products
const fetchEcomExternalProductCreateOptions = (dispatch) => async (
  pkEcomExternalConnectionType,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomExternalProductCreateOptions',
      url: `/api/staff/ecommerce/external/${pkEcomExternalConnectionType}/products/options`,
      type: 'get',
      successAction: 'replace',
      removeFilter: (item) =>
        pkEcomExternalConnectionType !== item.pkEcomExternalConnectionType
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedEcomExternalProducts = (dispatch) => async (
  { search, status, fkEcomCategory, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomExternalProducts',
      url: `/api/staff/ecommerce/external/products`,
      type: 'get',
      params: {
        search,
        status,
        fkEcomCategory,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchEcomExternalProduct = (dispatch) => async (
  pkEcomExternalProduct,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomExternalProduct',
      url: `/api/staff/ecommerce/external/products/${pkEcomExternalProduct}`,
      type: 'get',
      filter: { pkEcomExternalProduct },
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchEcomExternalProductsForImport = (dispatch) => async (
  pkEcomExternalConnectionType,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      url: `/api/staff/ecommerce/external/${pkEcomExternalConnectionType}/products/import`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchEcomExternalProductExportData = (dispatch) => async (
  {
    pkEcomExternalProducts,
    search,
    status,
    fkEcomCategory,
    skipStateUpdate = false
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const params = new URLSearchParams();

  if (search) {
    params.append('search', search);
  }

  if (status) {
    params.append('status', status);
  }

  if (fkEcomCategory) {
    params.append('fkEcomCategory', fkEcomCategory);
  }

  if (pkEcomExternalProducts) {
    pkEcomExternalProducts?.forEach((uuid, index) => {
      params.append(`pkEcomExternalProducts[${index}]`, uuid);
    });
  }

  onApiRequest(
    {
      url: `/api/staff/ecommerce/external/products/export-data?${params.toString()}`,
      type: 'get',
      ...(skipStateUpdate
        ? {}
        : {
            dispatch,
            stateName: 'ecomExternalProductsExport',
            filter: { pkEcomExternalProducts, search, status, fkEcomCategory },
            resetStateValue: []
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const importEcomExternalProducts = (dispatch) => async (
  pkEcomExternalConnectionType,
  { ecomExternalProducts },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomExternalProducts',
      url: `/api/staff/ecommerce/external/${pkEcomExternalConnectionType}/products/import`,
      type: 'post',
      data: ecomExternalProducts,
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const linkEcomExternalProducts = (dispatch) => async (
  pkEcomExternalConnectionType,
  { syncAfterLink, syncOptions },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/external/${pkEcomExternalConnectionType}/products/link-to-printify`,
      type: 'post',
      data: {
        syncAfterLink,
        syncOptions
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateEcomExternalProducts = (dispatch) => async (
  { ecomExternalProducts, selectFirstItem },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: selectFirstItem
        ? 'ecomExternalProduct'
        : 'ecomExternalProductsExport',
      url: `/api/staff/ecommerce/external/products`,
      type: 'put',
      data: ecomExternalProducts,
      selectFirstItem,
      successAction: selectFirstItem ? 'success' : 'replace',
      removeFilter: (item) =>
        ecomExternalProducts.every(
          (p) =>
            p.pkEcomExternalProduct.toString() !==
            item.pkEcomExternalProduct?.toString()
        )
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteEcomExternalProducts = (dispatch) => async (
  pkEcomExternalProducts,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkEcomExternalProductsParam = pkEcomExternalProducts?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkEcomExternalProducts[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomExternalProducts',
      url: `/api/staff/ecommerce/external/products?${pkEcomExternalProductsParam}`,
      type: 'delete',
      removeFilter: (item) =>
        !pkEcomExternalProducts.includes(item.pkEcomExternalProduct)
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const syncEcomExternalProductsWithProviders = (dispatch) => async (
  { fkEcomExternalConnectionType, products, syncOptions },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/external/${fkEcomExternalConnectionType}/products/sync`,
      type: 'post',
      data: {
        fkEcomExternalConnectionType,
        products,
        syncOptions
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const syncEcomExternalProductsWithVendors = (dispatch) => async (
  { fkEcomExternalConnectionTypes, externalProductIds, syncOptions },
  onProgressCallback
) => {
  const syncPaginatedProducts = () => {
    const MAX_PRODUCTS_PER_BATCH = 25;
    const pageLength = Math.ceil(
      externalProductIds.length / MAX_PRODUCTS_PER_BATCH
    );

    let lastPercent = 0;
    let lastRemainingProductCount = externalProductIds.length;

    return new Promise((resolve, reject) => {
      const paginatePost = (page = 1) => {
        const productIds = externalProductIds.slice(
          (page - 1) * MAX_PRODUCTS_PER_BATCH,
          page * MAX_PRODUCTS_PER_BATCH
        );

        lastPercent = Math.floor(((page - 1) / pageLength) * 100);
        lastRemainingProductCount =
          externalProductIds.length - (page - 1) * MAX_PRODUCTS_PER_BATCH;
        onProgressCallback({
          completed: false,
          percent: lastPercent,
          remainingProductCount: lastRemainingProductCount
        });

        onApiRequest(
          {
            url: `/api/staff/ecommerce/external/products/vendors/sync`,
            type: 'post',
            data: {
              fkEcomExternalConnectionTypes,
              externalProductIds: productIds,
              syncOptions
            }
          },
          () => {
            if (page < pageLength) {
              paginatePost(page + 1);
              return;
            }

            onProgressCallback({
              completed: true,
              percent: 100,
              remainingProductCount: 0
            });
          },
          (e) => {
            reject(e);
            onProgressCallback({
              completed: false,
              failed: true,
              failedMessage: e,
              percent: lastPercent,
              remainingProductCount: lastRemainingProductCount
            });
          }
        );
      };

      paginatePost();
    });
  };

  const syncPaginatedConnectionTypes = () => {
    let lastPercent = 0;

    const calculateProgress = ({
      connectionTypeIndex,
      connectionTypes,
      skippedExternalIds,
      totalExternalIds,
      pageIndex,
      totalPages
    }) => {
      const totalConnections = connectionTypes.length;

      const completedConnections = connectionTypeIndex - 1;
      const completedExternalIds = skippedExternalIds.length;
      const pageProgress = totalPages > 0 ? pageIndex / totalPages : 0;

      const rawPercent =
        ((completedConnections +
          (completedExternalIds + pageProgress) / totalExternalIds) /
          totalConnections) *
        100;

      // Never allow progress to go backwards
      lastPercent = Math.max(lastPercent, Math.floor(rawPercent));

      return lastPercent;
    };

    return new Promise((resolve, reject) => {
      const connectionTypes =
        fkEcomExternalConnectionTypes ||
        EXTERNAL_CONNECTION_CONFIGS.map((c) => c.pkEcomExternalConnectionType);

      let skippedExternalIds = [];

      const paginatePost = (connectionTypeIndex = 1, page = 1) => {
        const fkEcomExternalConnectionType =
          connectionTypes[connectionTypeIndex - 1];

        onProgressCallback({
          completed: false,
          percent: lastPercent,
          fkEcomExternalConnectionType
        });

        onApiRequest(
          {
            url: `/api/staff/ecommerce/external/products/vendors/sync`,
            type: 'post',
            data: {
              page,
              skippedExternalIds,
              fkEcomExternalConnectionTypes: [fkEcomExternalConnectionType],
              syncOptions
            }
          },
          (d) => {
            if (d) {
              calculateProgress({
                connectionTypeIndex,
                connectionTypes,
                skippedExternalIds,
                totalExternalIds:
                  d.remainingExternalIds.length + skippedExternalIds.length + 1,
                pageIndex: d.paginatedResponse.pageIndex,
                totalPages: d.paginatedResponse.totalPages
              });

              // Still paging within the SAME external ID
              if (
                d.paginatedResponse.pageIndex < d.paginatedResponse.totalPages
              ) {
                paginatePost(connectionTypeIndex, page + 1);
                return;
              }

              // Finished this external ID, move to next
              if (d.remainingExternalIds.length > 0) {
                skippedExternalIds.push(d.externalId);
                paginatePost(connectionTypeIndex);
                return;
              }

              // Finished ALL external IDs for this connection, move to next
              if (connectionTypeIndex < connectionTypes.length) {
                skippedExternalIds = [];
                paginatePost(connectionTypeIndex + 1);
                return;
              }
            }

            // Fully complete
            onProgressCallback({
              completed: true,
              percent: 100
            });
            resolve();
          },
          (e) => {
            onProgressCallback({
              completed: false,
              failed: true,
              failedMessage: e,
              percent: lastPercent
            });
            reject(e);
          }
        );
      };

      paginatePost();
    });
  };

  if (externalProductIds) {
    syncPaginatedProducts();
  }
  else {
    syncPaginatedConnectionTypes();
  }
};

const mergeEcomExternalProducts = (dispatch) => async (
  {
    fkEcomExternalConnectionType,
    pkEcomExternalProduct,
    pkEcomExternalProducts
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/external/${fkEcomExternalConnectionType}/products/merge`,
      type: 'post',
      data: {
        fkEcomExternalConnectionType,
        pkEcomExternalProduct,
        pkEcomExternalProducts
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Orders
const fetchPaginatedEcomOrders = (dispatch) => async (
  {
    search,
    status,
    fkEcomExternalConnectionType,
    sort,
    page,
    pageSize,
    appendResults
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomOrders',
      url: `/api/staff/ecommerce/orders`,
      type: 'get',
      params: {
        search,
        status,
        fkEcomExternalConnectionType,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const submitEcomOrderExternalOrders = (dispatch) => async (
  { pkEcomOrders, fkEcomExternalConnectionType },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/orders/submit-external`,
      type: 'post',
      data: { pkEcomOrders, fkEcomExternalConnectionType }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateEcomOrderExternalConnectionId = (dispatch) => async (
  pkEcomOrder,
  { externalId, fkEcomExternalConnectionType, ecomExternalConnectionId },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/orders/${pkEcomOrder}/externalId`,
      type: 'put',
      data: {
        externalId,
        fkEcomExternalConnectionType,
        ecomExternalConnectionId
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const cancelEcomOrderExternalOrders = (dispatch) => async (
  { pkEcomOrders, fkEcomExternalConnectionType },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/orders/cancel-external`,
      type: 'post',
      data: {
        pkEcomOrders,
        fkEcomExternalConnectionType
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Customers
const fetchPaginatedAdminCustomers = (dispatch) => async (
  { search, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomCustomers',
      url: `/api/staff/ecommerce/customers`,
      type: 'get',
      params: {
        search,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminCustomer = (dispatch) => async (
  { pkEcomCustomer },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomCustomer',
      url: `/api/staff/ecommerce/customers/${pkEcomCustomer}`,
      type: 'get',
      successAction: 'success',
      filter: { pkEcomCustomer },
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Vendors
const fetchEcomVendor = (dispatch) => async (
  pkEcomVendor,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendor',
      url: `/api/staff/ecommerce/vendors/${pkEcomVendor}`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedEcomVendors = (dispatch) => async (
  { search, status, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendors',
      url: `/api/staff/ecommerce/vendors/summaries`,
      type: 'get',
      params: {
        search,
        status,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedEcomVendorPayouts = (dispatch) => async (
  {
    pkEcomVendor,
    search,
    startDate,
    endDate,
    sort,
    page,
    pageSize,
    skipLoading,
    appendResults
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorPayouts',
      url: `/api/staff/ecommerce/vendors/payouts`,
      type: 'get',
      params: {
        pkEcomVendor,
        search,
        startDate,
        endDate,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchEcomVendorPayout = (dispatch) => async (
  pkEcomVendor,
  { pkEcomVendorPayout, startDate, endDate },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorPayout',
      url: `/api/staff/ecommerce/vendors/${pkEcomVendor}/payouts`,
      type: 'get',
      params: { pkEcomVendorPayout, startDate, endDate }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createEcomVendorPayout = (dispatch) => async (
  pkEcomVendor,
  { chequeId, description, pkEcomOrders, payoutAmount },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/vendors/${pkEcomVendor}/payouts`,
      type: 'post',
      data: { chequeId, description, pkEcomOrders, payoutAmount }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Reports & Analytics
const fetchAdminAnalyticsOverview = (dispatch) => async (
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomAdminAnalyticsOverview',
      url: `/api/staff/ecommerce/analytics/overview`,
      type: 'get',
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminAnalytics = (dispatch) => async (
  {
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    pkEcomStore,
    pkEcomVendor
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomAdminAnalytics',
      url: `/api/staff/ecommerce/analytics`,
      type: 'get',
      successAction: 'success',
      params: {
        startDate,
        endDate,
        compareStartDate,
        compareEndDate,
        pkEcomStore,
        pkEcomVendor
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const generateAdminReport = (dispatch) => async (
  {
    uuid,
    noDateRange,
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    periodInterval,
    groupBy,
    headers,
    options,
    pkEcomStore,
    pkEcomVendor,
    filters
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomAdminReport',
      url: `/api/staff/ecommerce/reports/generate/${uuid}`,
      type: 'post',
      successAction: 'success',
      data: {
        noDateRange,
        startDate,
        endDate,
        compareStartDate,
        compareEndDate,
        periodInterval,
        groupBy,
        headers,
        options,
        pkEcomStore,
        pkEcomVendor,
        filters
      },
      forceLoading: true
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Stock Content
const fetchPaginatedEcomStockContent = (dispatch) => async (
  { search, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomStockContent',
      url: `/api/staff/ecommerce/stock-content`,
      type: 'get',
      params: {
        search,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchEcomStockContent = (dispatch) => async (
  pkEcomStockContent,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomStockContent',
      successAction: 'success',
      url: `/api/staff/ecommerce/stock-content/${pkEcomStockContent}`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createEcomStockContent = (dispatch) => async (
  ecomStockContent,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/stock-content`,
      type: 'post',
      data: ecomStockContent
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateEcomStockContent = (dispatch) => async (
  { ecomStockContent, selectFirstItem },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/staff/ecommerce/stock-content`,
      type: 'put',
      data: ecomStockContent,
      ...(selectFirstItem
        ? {
            dispatch,
            stateName: 'ecomStockContent',
            successAction: 'success',
            selectFirstItem
          }
        : {})
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteEcomStockContent = (dispatch) => async (
  pkEcomStockContents,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const searchParams = buildURLSearchParams({ pkEcomStockContents });
  onApiRequest(
    {
      url: `/api/staff/ecommerce/stock-content?${searchParams.toString()}`,
      type: 'delete'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedEcomStockContentStores = (dispatch) => async (
  pkEcomStockContent,
  { search, status, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomStockContentStores',
      url: `/api/staff/ecommerce/stock-content/${pkEcomStockContent}/stores`,
      type: 'get',
      params: {
        pkEcomStockContent,
        search,
        status,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

export const { Provider, Context } = createDataProvider(
  providerContextReducer,
  {
    cancelEcomOrderExternalOrders,
    createEcomStockContent,
    createEcomVendorPayout,
    createOfflineEcomStoreEscrowPayment,
    deleteEcomExternalProducts,
    deleteEcomStockContent,
    fetchAdminAnalytics,
    fetchAdminAnalyticsOverview,
    fetchAdminCustomer,
    fetchAdminResourceOptions,
    fetchAdminStoreEscrowOverview,
    fetchEcomExternalProduct,
    fetchEcomExternalProductCreateOptions,
    fetchEcomExternalProductsForImport,
    fetchEcomExternalProductExportData,
    fetchEcomStockContent,
    fetchEcomStore,
    fetchEcomStores,
    fetchEcomVendor,
    fetchEcomVendorPayout,
    fetchPaginatedAdminCustomers,
    fetchPaginatedEcomExternalProducts,
    fetchPaginatedEcomOrders,
    fetchPaginatedEcomStockContent,
    fetchPaginatedEcomStockContentStores,
    fetchPaginatedEcomStores,
    fetchPaginatedEcomVendorPayouts,
    fetchPaginatedEcomVendors,
    generateAdminReport,
    importEcomExternalProducts,
    linkEcomExternalProducts,
    mergeEcomExternalProducts,
    searchForUsers,
    submitEcomOrderExternalOrders,
    syncEcomExternalProductsWithProviders,
    syncEcomExternalProductsWithVendors,
    updateEcomExternalProducts,
    updateEcomOrderExternalConnectionId,
    updateEcomStockContent
  },
  initialState
);
